import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html, nothing } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { ProductDataChunkEntity } from '../../entities/productdatachunk.js';

export const KnowledgebaseDashboard = stateful(() => {
  // Use useState for the selected conversation and filters
  const [searchText, setSearchText] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [hasTypeState, setHasTypeState] = useState<'all' | 'product' | 'category'>('all');

  // Function to clear all filters
  const clearFilters = () => {
    setSearchText('');
    setStartDate('');
    setEndDate('');
    setHasTypeState('all');
  };

  const conversationDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    if (hasTypeState === 'product') {
      filterAspects.push(new BooleanAspect({ fieldName: 'hasType', value: true }));
    } else if (hasTypeState === 'category') {
      filterAspects.push(new BooleanAspect({ fieldName: 'hasType', value: false }));
    }

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }

    return [
      buildFullOptions<typeof ProductDataChunkEntity>({
        class: ProductDataChunkEntity,
        customColumns: [
          {
            fieldName: 'article_nr',
            columnName: 'Article Number',
            columnWidth: '140px',
            renderItem: value => html` ${value.article_nr || 'N/A'} `,
          },
          {
            fieldName: 'category',
            columnName: 'Category',
            columnWidth: '250px',
            renderItem: value => html` ${value.category || 'N/A'} `,
          },
          {
            fieldName: 'knowledge_type',
            columnName: 'Knowledge Type',
            columnWidth: '150px',
            renderItem: value => html` ${value.knowledge_type || 'N/A'} `,
          },
          {
            fieldName: 'name',
            columnName: 'Name',
            renderItem: value => html` ${value.name || 'N/A'} `,
          },
          {
            fieldName: 'product_type',
            columnName: 'Product Type',
            columnWidth: '250px',
            renderItem: value => html` ${value.product_type || 'N/A'} `,
          },
          {
            fieldName: 'createdAt',
            columnName: 'Created At',
            columnWidth: '175px',
            renderItem: value => format(value.createdAt ?? new Date(), 'Pp'),
          },
        ],
        isSortable: false,
        view: new View({
          filter: new AdornisFilter({
            searchFields: ['article_nr', 'category', 'name', 'product_type'],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [searchText, startDate, endDate, hasTypeState]);

  return html`
    <div class="flex gap-4 h-full">
      <div class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto w-full">
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Knowledgebase - Products and Categories</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Text Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search in conversations..."
                        class="input input-bordered input-sm w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Date Range - Combined in one container to keep them together -->
                    <div class="form-control col-span-1 md:col-span-2">
                      <label class="label">
                        <span class="label-text font-medium">Date Range</span>
                      </label>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="Start date"
                            class="input input-bordered input-sm w-full"
                            .value=${startDate}
                            @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="End date"
                            class="input input-bordered input-sm w-full"
                            .value=${endDate}
                            @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Filter Has Type -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Has Type</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${hasTypeState}
                        @change=${(e: InputEvent) =>
                          setHasTypeState((e.target as HTMLSelectElement).value as 'all' | 'product' | 'category')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Product</option>
                        <option value="no">Category</option>
                      </select>
                    </div>

                    <!-- Filter Actions -->
                    <div class="col-start-4 flex justify-end mt-4">
                      <button class="btn btn-sm btn-outline btn-error mr-2" @click=${clearFilters}>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing}

          <div class="overflow-x-auto">
            ${guard([conversationDataviewOptions], () =>
              DataTable({
                options: conversationDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  `;
});
